import { useTranslation } from "react-i18next";
import type { PaymentMethodsOut } from "../api";
import { fmtMoney } from "../api";
import { InfoTooltip } from "./info-tooltip";
import { Card, CardContent } from "./ui/card";
import { knownPaymentMethods } from "../lib/payment-method-summary";

/**
 * "Bu davrda pul qanday kirdi" — usullar bo'yicha bo'linish.
 *
 * Asosiy qarorlar:
 *
 * 1. Odatda **Noma'lum guruhi ko'rinadi**. Ish jurnali `knownMethodsOnly`
 *    bilan undan ataylab voz kechadi va jami/ulushni faqat ma'lum usullardan
 *    qayta hisoblaydi; backend qiymati va tarixiy yozuvlar o'zgarmaydi.
 * 2. **Nol guruhlar ham chiziladi** — jadval shakli davrdan davrga
 *    o'zgarmasligi kerak, aks holda ustunlar sakrab, taqqoslash qiyinlashardi.
 *    Faqat bo'sh DAVR (jami 0) qisqa jumla bilan almashtiriladi.
 *  3. **Ulush jamiga nisbatan** — Σ(guruhlar) == jami, ya'ni yonidagi
 *    "to'langan" raqamning aynan o'zi ajratiladi.
 */
export function PaymentMethodBreakdown({
  data,
  /** Ekran sarlavhasi ostidagi izoh matni (namespace'ga xos). */
  title,
  tooltip,
  knownMethodsOnly = false,
}: {
  data: PaymentMethodsOut | null;
  title: string;
  tooltip: string;
  /** Ish jurnalida legacy noma'lum qiymatlarni saqlab, faqat UI'dan yashiradi. */
  knownMethodsOnly?: boolean;
}) {
  const { t } = useTranslation("common");
  if (data === null) return null;

  const known = knownPaymentMethods(data);
  const buckets = knownMethodsOnly ? known.buckets : data.buckets;
  const jami = knownMethodsOnly ? known.total : parseFloat(data.jami) || 0;

  return (
    <Card className="payment-breakdown">
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>{title}</span>
          <InfoTooltip text={tooltip} />
          <span className="ml-auto font-num text-sm font-bold tabular-nums text-foreground">
            {fmtMoney(jami)}
          </span>
        </div>

        {(data.unresolved_returns_count ?? 0) > 0 && <p className="text-xs text-muted-foreground">{t("paymentMethod.unresolvedReturns", { count: data.unresolved_returns_count })}</p>}
        {jami === 0 && !knownMethodsOnly && !buckets.some((b) => b.soni > 0 || (b.refund_count ?? 0) > 0 || Number(b.summa) !== 0) ? (
          <p className="text-sm text-muted-foreground">
            {t("paymentMethod.emptyPeriod")}
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {buckets.map((b) => {
              const summa = parseFloat(b.summa) || 0;
              const ulush = jami > 0 ? Math.round((summa / jami) * 100) : 0;
              const nomalum = b.usul === null;
              return (
                <div
                  key={b.usul ?? "nomalum"}
                  className={
                    "payment-breakdown-item rounded-md border px-3 py-1.5 " +
                    // Noma'lum — e'tibor talab qiladigan guruh, lekin xato
                    // emas: neytral ogohlantirish uslubi.
                    (nomalum && summa > 0
                      ? "is-warning border-amber-500/40 bg-amber-500/5"
                      : "bg-muted/30")
                  }
                >
                  <div className="truncate text-xs text-muted-foreground">
                    {nomalum
                      ? t("paymentMethod.nomalum")
                      : t(`paymentMethod.${b.usul}`)}
                  </div>
                  <div className="font-num text-sm font-semibold tabular-nums">
                    {fmtMoney(b.summa)}
                  </div>
                  <div className="text-[11px] tabular-nums text-muted-foreground">
                    {jami > 0 ? `${ulush}% · ` : ""}{t("paymentMethod.docCount", { count: b.soni })}
                    {Number(b.refunds) > 0 && <div>{t("paymentMethod.refunds")}: {fmtMoney(b.refunds!)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
